import { defineComponent as _defineComponent } from 'vue'
import { computed, onMounted, PropType, ref } from 'vue';
import { hasNoNullTextItem } from '@/lib/rule';
import { now } from '@/utils/datetime';
import { FormItem } from '@/interfaces/lib/model';
import { SdsProduct } from '@/interfaces/model/general/sds';
import { VForm } from 'vuetify/lib/components';
import { defineMessages } from '@/lib/i18n';
import sharedMessages from '@/lib/shared-messages';
import { i18n } from '@/plugins/vue-i18n';
import { splitTextWithLinks } from '@/utils/splitTextWithLinks';
import { Section2ClassificationSystemId } from '@/lib/enum';
import { useMSdsSection2 } from '@/composables/useMSdsSection2';


export default /*#__PURE__*/_defineComponent({
  __name: 'SdsTableSection1ProductInfo',
  props: {
  productFormItems: {
    type: Array as PropType<Array<FormItem>>,
    value: () => [],
    required: true,
  },
  isEditing: {
    type: Boolean,
    default: false,
  },
  productIndex: {
    type: Number,
    default: 0,
  },
  products: {
    type: Array as PropType<Array<SdsProduct>>,
    default: () => [],
  },
},
  emits: ["update:isValid", "update:publishedAt", "update:revisedAt", "update:products", "update:productIndex", "update:productFormItems"],
  setup(__props, { emit: emits }: { emit: ({
  (e: 'update:isValid', data: boolean): void;
  (e: 'update:publishedAt', data: string | null): void;
  (e: 'update:revisedAt', data: string | null): void;
  (e: 'update:products', data: Array<SdsProduct>): void;
  (e: 'update:productIndex', data: number): void;
  (e: 'update:productFormItems', data: Array<FormItem>): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props;

const $t = i18n.t.bind(i18n);

const m = defineMessages({
  productInformation: '製品情報',
  addProduct: '製品を追加する',
  inputProductName: '製品名を入力してください',
  noDataText: '製品が未入力です',
});



const selectedItem = computed((): FormItem => {
  if (props.productFormItems.length == 0) {
    return { text: String($t(m.noDataText)), value: 0, disabled: true };
  }
  return props.productFormItems[props.productIndex];
});
const isValid = ref<boolean>(false);
const form = ref<InstanceType<typeof VForm> | null>(null);
const modalDeleteProduct = ref<boolean>(false);

const openDeleteProductModal = (): void => {
  modalDeleteProduct.value = true;
};

const closeDeleteProductModal = (): void => {
  modalDeleteProduct.value = false;
};



const updateIsValid = (e: boolean) => {
  emits('update:isValid', e);
};

const updateProductIndex = (e: FormItem) => {
  emits('update:productIndex', e.value as number);
};

const updateProduct = (key: string, value: string) => {
  const updatedData = [...props.products];
  updatedData[props.productIndex][key] = value;
  emits('update:products', updatedData);
};

const getProductFieldValue = (key: string): string => {
  if (!props.products.length || props.productIndex >= props.products.length) {
    return '';
  }
  const product = props.products[props.productIndex];
  if (!product) {
    return '';
  }
  const value = (product as unknown as Record<string, unknown>)[key];
  return typeof value === 'string' ? value : '';
};

const deleteProduct = (): void => {
  const updatedData = [...props.products];
  updatedData[props.productIndex].deleted_at = now().toString();
  updatedData[props.productIndex].sds_section2_label.deleted_at =
    now().toString();
  for (const x of updatedData[props.productIndex].sds_section1_manufacturers) {
    x.deleted_at = now().toString();
  }
  for (const x of updatedData[props.productIndex].sds_section1_importers) {
    x.deleted_at = now().toString();
  }
  for (const x of updatedData[props.productIndex].sds_section2_list) {
    x.deleted_at = now().toString();
    for (const y of x.sds_section2_hazard_list) {
      y.deleted_at = now().toString();
    }
  }
  updatedData[props.productIndex].sds_section3.deleted_at = now().toString();
  for (const x of updatedData[props.productIndex].sds_section3.material_list) {
    x.deleted_at = now().toString();
    for (const y of x.cas_list) {
      y.deleted_at = now().toString();
    }
  }
  for (const x of updatedData[props.productIndex].sds_section3.impurity_list) {
    x.deleted_at = now().toString();
    for (const y of x.cas_list) {
      y.deleted_at = now().toString();
    }
  }
  for (const x of updatedData[props.productIndex].sds_section8_acgih_list) {
    x.deleted_at = now().toString();
    for (const y of x.material_list) {
      y.deleted_at = now().toString();
      for (const z of y.cas_list) {
        z.deleted_at = now().toString();
      }
    }
  }
  for (const x of updatedData[props.productIndex].sds_section8_jsoh_list) {
    x.deleted_at = now().toString();
    for (const y of x.material_list) {
      y.deleted_at = now().toString();
      for (const z of y.cas_list) {
        z.deleted_at = now().toString();
      }
    }
  }
  for (const x of updatedData[props.productIndex].sds_section8_dnel_dmel_list) {
    x.deleted_at = now().toString();
    for (const y of x.material_list) {
      y.deleted_at = now().toString();
      for (const z of y.cas_list) {
        z.deleted_at = now().toString();
      }
    }
  }
  for (const x of updatedData[props.productIndex].sds_section9_list) {
    x.deleted_at = now().toString();
  }
  for (const x of updatedData[props.productIndex]
    .sds_section9_flash_point_list) {
    x.deleted_at = now().toString();
  }
  for (const x of updatedData[props.productIndex]
    .sds_section9_boiling_point_list) {
    x.deleted_at = now().toString();
  }
  for (const x of updatedData[props.productIndex].sds_section15_list) {
    x.deleted_at = now().toString();
    for (const y of x.material_list) {
      y.deleted_at = now().toString();
      for (const z of y.cas_list) {
        z.deleted_at = now().toString();
      }
    }
  }

  const formItems = updatedData.map((x, idx) => {
    return {
      text: `${x.name}`,
      value: idx,
      disabled: props.productIndex == idx ? true : false,
    };
  });

  emits('update:productFormItems', formItems);

  emits('update:productIndex', props.productIndex == 0 ? 1 : 0);
};

const updateProductName = (e: string, index: number) => {
  if (!props.products.length) {
    return;
  }
  const updatedData = [...props.products];
  updatedData[index].name = e;
  emits('update:products', updatedData);

  const formItems = updatedData.map((x, idx) => {
    return {
      text: x.name,
      value: idx,
      disabled: !!x.deleted_at,
    };
  });
  emits('update:productFormItems', formItems);
};

const addProduct = (): void => {
  const updatedData = [...props.products];

  updatedData.push({
    id: 0,
    name: String($t(m.inputProductName)),
    recommended_use: '',
    restriction: '',
    recommended_use_restriction: '',
    sds_id: '',
    sds_section2_label: {
      id: 0,
      sds_id: '',
      sds_product_id: 0,
      bomb: false,
      fire: false,
      fire_circle: false,
      gas: false,
      toxic: false,
      corrosion: false,
      exclamation: false,
      health: false,
      environment: false,
      signal_words: 'なし',
      signal_word_id: 'NONE',
      created_at: '2022-10-03T06:40:48.580289',
      updated_at: '2022-10-03T06:40:48.580289',
      deleted_at: null,
    },
    sds_section2_list: getItemsByClassificationSystemId(
      Section2ClassificationSystemId.GHS.id
    ).map((x) => {
      return {
        id: 0,
        sds_id: '',
        sds_product_id: 0,
        item_id: x.id,
        category_id: 0,
        classification_system_id: Section2ClassificationSystemId.GHS.id,
        material_name: null,
        sds_section2_hazard_list: [],
        created_at: '',
        updated_at: '',
        deleted_at: null,
      };
    }),
    sds_section3: {
      id: 0,
      sds_id: '',
      sds_product_id: 0,
      composition_id: 0,
      synonyms: [],
      aliases: [],
      chemical_properties: [],
      material_list: [],
      impurity_list: [],
      created_at: '',
      updated_at: '',
      deleted_at: null,
    },
    sds_section1_manufacturers: [],
    sds_section1_importers: [],
    sds_section9_list: [],
    sds_section9_boiling_point_list: [],
    sds_section9_flash_point_list: [],
    sds_section8_dnel_dmel_list: [],
    sds_section8_jsoh_list: [],
    sds_section8_acgih_list: [],
    sds_section15_list: [],
    created_at: '',
    updated_at: '',
    deleted_at: null,
  });

  emits('update:products', updatedData);

  const formItems = updatedData.map((x, idx) => {
    return {
      text: x.name,
      value: idx,
      disabled: !!x.deleted_at,
    };
  });
  emits('update:productFormItems', formItems);

  emits('update:productIndex', formItems.length - 1);
};
const { getItemsByClassificationSystemId, load: loadMSection2 } =
  useMSdsSection2();

onMounted((): void => {
  loadMSection2();
  if (form.value) {
    form.value.validate();
  }
});

return { __sfc: true,$t, m, props, selectedItem, isValid, form, modalDeleteProduct, openDeleteProductModal, closeDeleteProductModal, emits, updateIsValid, updateProductIndex, updateProduct, getProductFieldValue, deleteProduct, updateProductName, addProduct, getItemsByClassificationSystemId, loadMSection2, hasNoNullTextItem, VForm, sharedMessages, splitTextWithLinks }
}

})